import { openDB, IDBPDatabase, DBSchema } from 'idb';

interface VocabularyItem {
  id: string;
  user_id: string;
  source_text: string;
  target_translation: string;
  language: string; // Language code (e.g., 'spanish', 'french', 'italian')
  source_language: string; // Native language code (e.g., 'english', 'french')
  is_manual: boolean;
  created_at: string;
  next_review_at: string | null;
  interval_days: number | null;
  ease_factor: number | null;
  repetitions: number | null;
  last_reviewed_at: string | null;
  image_url: string | null;
  // FSRS fields
  stability: number | null;
  difficulty: number | null;
  state: number | null;
  lapses: number | null;
  learning_steps: number | null;
}

export interface PendingReview {
  id?: number;
  cardId: string;
  rating: 'again' | 'hard' | 'good' | 'easy';
  userId: string;
  tier: 'free' | 'premium' | 'admin' | 'paid' | 'teacher';
  timestamp: string;
  // Updated card state after review
  updatedFields: {
    interval_days: number;
    ease_factor: number;
    repetitions: number;
    next_review_at: string;
    last_reviewed_at: string;
    stability?: number;
    difficulty?: number;
    state?: number;
    lapses?: number;
    learning_steps?: number;
  };
}

interface AppState {
  key: string;
  value: unknown;
}

interface LekaDB extends DBSchema {
  vocabulary: {
    key: string;
    value: VocabularyItem;
    indexes: {
      'by-user': string;
      'by-next-review': string;
    };
  };
  pendingSync: {
    key: number;
    value: PendingReview;
    indexes: {
      'by-user': string;
    };
  };
  appState: {
    key: string;
    value: AppState;
  };
}

const DB_NAME = 'leka-offline';
const DB_VERSION = 3; // Incremented for RFC 018 (language column)
export const SCHEMA_VERSION = 5; // Incremented for RFC 056 (english_word → source_text)

let dbInstance: IDBPDatabase<LekaDB> | null = null;

export async function initDB(): Promise<IDBPDatabase<LekaDB>> {
  if (dbInstance) return dbInstance;

  dbInstance = await openDB<LekaDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // Vocabulary store
      if (!db.objectStoreNames.contains('vocabulary')) {
        const vocabStore = db.createObjectStore('vocabulary', { keyPath: 'id' });
        vocabStore.createIndex('by-user', 'user_id');
        vocabStore.createIndex('by-next-review', 'next_review_at');
      }

      // Pending sync store
      if (!db.objectStoreNames.contains('pendingSync')) {
        const syncStore = db.createObjectStore('pendingSync', { 
          keyPath: 'id', 
          autoIncrement: true 
        });
        syncStore.createIndex('by-user', 'userId');
      }

      // App state store
      if (!db.objectStoreNames.contains('appState')) {
        db.createObjectStore('appState', { keyPath: 'key' });
      }
    },
  });

  return dbInstance;
}

export async function getDB(): Promise<IDBPDatabase<LekaDB>> {
  if (!dbInstance) {
    return initDB();
  }
  return dbInstance;
}

export async function clearUserData(userId: string): Promise<void> {
  const db = await getDB();
  
  // Clear vocabulary for this user
  const vocabTx = db.transaction('vocabulary', 'readwrite');
  const vocabIndex = vocabTx.store.index('by-user');
  let vocabCursor = await vocabIndex.openCursor(IDBKeyRange.only(userId));
  while (vocabCursor) {
    await vocabCursor.delete();
    vocabCursor = await vocabCursor.continue();
  }
  await vocabTx.done;

  // Clear pending syncs for this user
  const syncTx = db.transaction('pendingSync', 'readwrite');
  const syncIndex = syncTx.store.index('by-user');
  let syncCursor = await syncIndex.openCursor(IDBKeyRange.only(userId));
  while (syncCursor) {
    await syncCursor.delete();
    syncCursor = await syncCursor.continue();
  }
  await syncTx.done;

  // Clear user-specific app state
  const stateTx = db.transaction('appState', 'readwrite');
  await stateTx.store.delete(`lastSync-${userId}`);
  await stateTx.done;
}

export async function clearAllData(): Promise<void> {
  const db = await getDB();
  
  const vocabTx = db.transaction('vocabulary', 'readwrite');
  await vocabTx.store.clear();
  await vocabTx.done;

  const syncTx = db.transaction('pendingSync', 'readwrite');
  await syncTx.store.clear();
  await syncTx.done;

  const stateTx = db.transaction('appState', 'readwrite');
  await stateTx.store.clear();
  await stateTx.done;
}

/**
 * Store auth token for service worker access
 */
export async function storeAuthToken(userId: string, token: string): Promise<void> {
  const db = await getDB();
  await db.put('appState', { key: `authToken-${userId}`, value: token });
  // Also store current user ID for service worker to know which user is logged in
  await db.put('appState', { key: 'currentUserId', value: userId });
}

/**
 * Get auth token for service worker
 */
export async function getAuthToken(userId: string): Promise<string | null> {
  const db = await getDB();
  const result = await db.get('appState', `authToken-${userId}`);
  return result?.value as string | null;
}

/**
 * Get current logged in user ID
 */
export async function getCurrentUserId(): Promise<string | null> {
  const db = await getDB();
  const result = await db.get('appState', 'currentUserId');
  return result?.value as string | null;
}

/**
 * Clear auth token on logout
 */
export async function clearAuthToken(userId: string): Promise<void> {
  const db = await getDB();
  await db.delete('appState', `authToken-${userId}`);
  await db.delete('appState', 'currentUserId');
  await db.delete('appState', `userTier-${userId}`);
}

/**
 * Store user tier for offline access
 */
export async function storeUserTier(userId: string, tier: string): Promise<void> {
  const db = await getDB();
  await db.put('appState', { key: `userTier-${userId}`, value: tier });
}

/**
 * Get cached user tier for offline access
 */
export async function getUserTier(userId: string): Promise<string | null> {
  const db = await getDB();
  const result = await db.get('appState', `userTier-${userId}`);
  return result?.value as string | null;
}

/**
 * Get the current schema version stored in IndexedDB
 */
export async function getStoredSchemaVersion(): Promise<number> {
  const db = await getDB();
  const result = await db.get('appState', 'schemaVersion');
  return (result?.value as number) || 1;
}

/**
 * Set the schema version in IndexedDB
 */
export async function setStoredSchemaVersion(version: number): Promise<void> {
  const db = await getDB();
  await db.put('appState', { key: 'schemaVersion', value: version });
}

export type { VocabularyItem, LekaDB };
